import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import { Reset } from 'styled-reset';
import { DndProvider } from 'react-dnd';
import Backend from 'react-dnd-html5-backend';
import { createSelector } from 'reselect';
import { SplitFactory, withSplitFactory } from '@splitsoftware/splitio-react';
import { ThemeProvider, Application, Icon, Div, Image, Text } from '@pinpoint/design-kit';
import * as Sentry from '@sentry/browser';
import { ProductFruits } from 'react-product-fruits';

import DefaultTheme from 'src/styles/DefaultTheme';

import { createGlobalStyle } from 'styled-components';

import GLStyle from 'src/styles';
import { fetchClientSftp, inlineLogout } from 'src/store/api';
import { setSftpAccess, subscribeSftpAccess } from 'src/store/sftp_access';
import { appActions } from 'src/store/actions';
import { ProtectedRoute } from 'src/components/hoc';
import CONSTANTS from 'src/store/constants';
import { BreadcrumbSwitch, BreadcrumbRoute, ClientsSelection, ErrorBoundary, Feedback, LoginSSO } from 'src/components';
import * as Pages from 'src/pages';
import { Breadcrumbs } from 'src/containers';

import { ROUTES, RBAC } from 'src/config';
import TokenExchange from 'src/components/TokenExchange';

const GlobalStyle = createGlobalStyle`
  @media (max-width: ${DefaultTheme.grid.breakpoints.lg}px) {
    html {
      font-size: 14px;
    }
  }
  @media (min-width: ${DefaultTheme.grid.breakpoints.lg}px) {
    html {
      font-size: 16px;
    }
  }
  body {
    font-family: Roboto;
    margin: 0;
    padding: 0;
  }
`;

const SPLITIO_CONFIG = {
  core: {
    authorizationKey: process.env.REACT_APP_SPLITIO_AUTHORIZATION_KEY || 'localhost',
    key: process.env.REACT_APP_SPLITIO_CUSTOMER_ID || 'CUSTOMER_ID',
  },
  // features is only enabled during local development when authorization key is 'localhost'
  features: {
    [CONSTANTS.SPLIT_NAMES.CAN_CREATE_THINKALIKE_MODELS]: 'on',
    [CONSTANTS.SPLIT_NAMES.CAN_VIEW_THINKALIKE_MODELS]: 'on',
    [CONSTANTS.SPLIT_NAMES.CAN_VIEW_ZIP_CODE_RANKING]: 'off',
  },
};

const menu = [
  {
    type: 'ITEM',
    icon: 'Company',
    name: 'Clients',
    color: 'blue',
    link: '/clients',
    'data-test': 'clients-nav-item',
    isHighlighted: (pathname) => pathname.startsWith('/clients'),
    isVisible: (client, profile) => [profile.role, profile.siteRole].some((role) => RBAC.PAGES.CLIENTS.includes(role)),
  },
  {
    type: 'ITEM',
    icon: 'Company',
    name: 'SFTP',
    color: 'violet',
    link: ROUTES.SFTP,
    'data-test': 'sftp-nav-item',
    isHighlighted: (pathname) => pathname.startsWith(ROUTES.SFTP),
    requiresSftp: true,
    isVisible: (client, profile) => [profile.role, profile.siteRole].some((role) => RBAC.PAGES.SFTP.includes(role)),
  },
  {
    type: 'ITEM',
    icon: 'Thinkalike',
    name: 'Users',
    color: 'blueLight',
    link: '/users',
    'data-test': 'users-nav-item',
    isVisible: (client, profile) => [profile.role, profile.siteRole].some((role) => RBAC.PAGES.USERS.includes(role)),
  },
  {
    type: 'ITEM',
    icon: 'Onboard',
    name: 'Onboarding',
    color: 'teal',
    link: '/onboarding_landing',
    'data-test': 'onb-nav-item',
    isHighlighted: (pathname) => pathname.startsWith('/onboarding'),
    isVisible: (client, profile) =>
      [profile.role, profile.siteRole].some((role) => RBAC.PAGES.ONBOARDING.includes(role)),
  },
  {
    type: 'ITEM',
    icon: 'Crm',
    name: 'Datasets',
    color: 'green',
    link: '/datasets',
    'data-test': 'datasets-nav-item',
    isHighlighted: (pathname) => pathname.startsWith('/datasets') || pathname.startsWith('/segments'),
    isVisible: (client, profile) => [profile.role, profile.siteRole].some((role) => RBAC.PAGES.DATASETS.includes(role)),
  },
  {
    type: 'ITEM',
    icon: 'ThinkalikeModel',
    name: 'Models',
    color: 'rioGrande',
    link: ROUTES.MODELS,
    'data-test': 'models-nav-item',
    isHighlighted: (pathname) => pathname.startsWith(ROUTES.MODELS),
    isVisible: (client, profile) => [profile.role, profile.siteRole].some((role) => RBAC.PAGES.MODELS.includes(role)),
  },
  {
    type: 'ITEM',
    icon: 'Activate',
    name: 'Real-Time API',
    color: 'marigold',
    link: '/rt',
    'data-test': 'rtapi-nav-item',
    isHighlighted: (pathname) => pathname.startsWith('/rt'),
    isVisible: (client, profile) =>
      [profile.role, profile.siteRole].some((role) => RBAC.PAGES.REAL_TIME_API.includes(role)) &&
      client.isRealTimeApiEnabled,
  },
  {
    type: 'ITEM',
    icon: 'Text',
    name: 'Copy Personalization',
    color: 'orange',
    link: '/copypx',
    'data-test': 'copypx-nav-item',
    isHighlighted: (pathname) => pathname.startsWith('/copy'),
    isVisible: (client, profile) =>
      client.canPersonalize && [profile.role, profile.siteRole].some((role) => RBAC.PAGES.COPY_PX.includes(role)),
  },
  {
    type: 'ITEM',
    icon: 'Regulatory',
    iconViewBox: '0 0 38 38',
    name: 'Regulatory',
    color: 'orange',
    link: '/regulatory',
    'data-test': 'regulatory-nav-item',
    isHighlighted: (pathname) => pathname.startsWith('/regulatory'),
    isVisible: (client, profile) =>
      [profile.role, profile.siteRole].some((role) => RBAC.PAGES.REGULATORY.includes(role)),
  },
];

const accessTokenFilter = ({ accessToken }) => accessToken;

const refreshTokenFilter = ({ refreshToken }) => refreshToken;

const isLoggedInSelector = createSelector(
  [accessTokenFilter, refreshTokenFilter],
  (accessToken, refreshToken) => accessToken && refreshToken
);

function App({
  app: { mini, fetchClientStatus, clients, client, profile, tenant },
  isLoggedIn,
  selectClient,
  toggleMenu,
}) {
  if (isLoggedIn) {
    Sentry.setUser({
      username: profile.username,
      email: profile.email,
      segment: profile.originalTenant,
      ip_address: '{{auto}}',
    });
  }

  const dispatch = useDispatch();
  const [sftpEnabled, setSftpEnabled] = useState(false);
  const sftpTenant = useRef('');
  const fetchClients = useCallback(() => dispatch(appActions.fetchClients()), [dispatch]);
  const fetchProfile = useCallback(() => dispatch(appActions.fetchProfile()), [dispatch]);

  useEffect(() => subscribeSftpAccess((next) => setSftpEnabled(next.enabled)), []);

  useEffect(() => {
    const currentTenant = tenant || '';
    if (sftpTenant.current !== currentTenant) {
      sftpTenant.current = currentTenant;
      setSftpAccess(currentTenant, false);
    }
    const roleAllowed = [profile && profile.role, profile && profile.siteRole].some((role) =>
      RBAC.PAGES.SFTP.includes(role)
    );
    if (!isLoggedIn || !currentTenant || !roleAllowed) return undefined;
    let active = true;
    fetchClientSftp(currentTenant)
      .then((data) => {
        if (active) setSftpAccess(currentTenant, !!data.enabled);
      })
      .catch(() => {
        if (active) setSftpAccess(currentTenant, false);
      });
    return () => {
      active = false;
    };
  }, [isLoggedIn, tenant, profile]);

  useEffect(() => {
    // NOTE: needs to run once on load
    if (isLoggedIn) {
      fetchProfile();
      fetchClients();
    }
  }, [fetchClients, fetchProfile, isLoggedIn]);

  return (
    <BrowserRouter>
      {![true, undefined].includes(profile?.username?.includes?.('jenkins')) && (
        <ProductFruits
          workspaceCode="G66U4zUIZtczchW9"
          language="en"
          user={{
            username: profile.username,
            firstname: profile.givenName,
            lastname: profile.familyName,
            email: profile.email,
          }}
        />
      )}
      <GlobalStyle />
      <GLStyle />
      <SplitFactory config={SPLITIO_CONFIG}>
        <ThemeProvider theme={DefaultTheme}>
          <Reset />
          <ErrorBoundary>
            <DndProvider backend={Backend}>
              {!isLoggedIn ? (
                <Switch>
                  <Route exact path="/login" component={Pages.Login} />
                  <Route exact path="/login/:cognitoClientId" component={LoginSSO} />
                  <Route exact path="/password-challenge" component={Pages.PasswordChallenge} />
                  <Route exact path="/forgot-password" component={Pages.ForgotPassword} />
                  <Route exact path="/change-password" component={Pages.ChangePassword} />
                  <Route exact path="/token" component={TokenExchange} />
                  <Route exact path="/token/:cognitoClientId" component={TokenExchange} />
                  <Route
                    path="/"
                    render={() => (
                      <Redirect
                        to={{
                          pathname: '/login',
                          state: { from: window.location.pathname, query: window.location.search },
                        }}
                      />
                    )}
                  />
                </Switch>
              ) : (
                <Application
                  variant="onpoint"
                  header={{
                    mini,
                    zIndex: '10',
                    onToggle: () => toggleMenu(!mini),
                    headerStyle: {
                      fontFamily: 'Roboto',
                    },
                    leftChildren: [
                      <BreadcrumbSwitch key="bc-switch">
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.SFTP}
                          component={Breadcrumbs.ClientSftpBreadcrumb}
                          allowedRoles={RBAC.PAGES.SFTP}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.CLIENTS}
                          component={Breadcrumbs.ClientsBreadcrumb}
                          allowedRoles={RBAC.PAGES.CLIENTS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.CLIENTS_ADD}
                          component={Breadcrumbs.AddClientBreadcrumb}
                          allowedRoles={RBAC.PAGES.CLIENTS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.CLIENTS_EDIT}
                          component={Breadcrumbs.EditClientBreadcrumb}
                          allowedRoles={RBAC.PAGES.CLIENTS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.CLIENTS_EDIT_SUBS}
                          component={Breadcrumbs.EditClientSubscribersBreadcrumb}
                          allowedRoles={RBAC.PAGES.CLIENTS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.USERS}
                          component={Breadcrumbs.UsersBreadcrumb}
                          allowedRoles={RBAC.PAGES.USERS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.USERS_ADD}
                          component={Breadcrumbs.NewUsersBreadcrumb}
                          allowedRoles={RBAC.PAGES.USERS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.USERS_VIEW}
                          component={Breadcrumbs.EditUsersBreadcrumb}
                          allowedRoles={RBAC.PAGES.USERS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.ONBOARDING_LANDING}
                          component={Breadcrumbs.OnboardingLandingBreadcrumb}
                          allowedRoles={RBAC.PAGES.ONBOARDING}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.ONBOARDING}
                          component={Breadcrumbs.OnboardingBreadcrumb}
                          allowedRoles={RBAC.PAGES.ONBOARDING}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.NEW_ONBOARDING}
                          component={Breadcrumbs.NewOnboardingBreadcrumb}
                          allowedRoles={RBAC.PAGES.ONBOARDING}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.RE_ONBOARDING}
                          component={Breadcrumbs.ReOnboardingBreadcrumb}
                          allowedRoles={RBAC.PAGES.ONBOARDING}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.ONBOARDING_VIEW}
                          component={Breadcrumbs.OnboardingViewBreadcrumbView}
                          allowedRoles={RBAC.PAGES.ONBOARDING}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.SEGMENTS}
                          component={Breadcrumbs.DatasetsBreadcrumb}
                          allowedRoles={RBAC.PAGES.DATASETS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.GENERATE_MODELS}
                          component={Breadcrumbs.GenerateModelsBreadcrumb}
                          allowedRoles={RBAC.PAGES.DATASETS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.COMPARE_SEGMENTS}
                          component={Breadcrumbs.SegmentsCompareBreadcrumb}
                          allowedRoles={RBAC.PAGES.DATASETS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.CLUSTERS}
                          component={Breadcrumbs.SegmentsViewBreadcrumb}
                          allowedRoles={RBAC.PAGES.DATASETS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.COMPOSER}
                          component={Breadcrumbs.SegmentsViewBreadcrumb}
                          allowedRoles={[CONSTANTS.ROLE.SUPER_ADMIN]}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.MODELS}
                          component={Breadcrumbs.ModelsListBreadcrumb}
                          allowedRoles={RBAC.PAGES.MODELS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.MODEL_LIFT_CHARTS}
                          component={Breadcrumbs.ModelViewBreadcrumb}
                          allowedRoles={RBAC.PAGES.MODELS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.MODELS_COMPARE}
                          component={Breadcrumbs.ModelComparisonBreadcrumb}
                          allowedRoles={RBAC.PAGES.MODELS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.MODEL_INPUT_DISTRIBUTIONS}
                          component={Breadcrumbs.ModelInputDistributionsBreadcrumb}
                          allowedRoles={RBAC.PAGES.MODELS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.MODEL_VALIDATION_ANALYTICS}
                          component={Breadcrumbs.ModelValidationAnalyticsBreadcrumb}
                          allowedRoles={RBAC.PAGES.MODELS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.MODEL_ROI_CALCULATOR_CMP}
                          component={Breadcrumbs.ModelImpactCalculatorComparisonBreadcrumb}
                          allowedRoles={RBAC.PAGES.MODELS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.MODEL_ROI_CALCULATOR}
                          component={Breadcrumbs.ModelRoiCalculatorBreadcrumb}
                          allowedRoles={RBAC.PAGES.MODELS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.MODEL_FAIRNESS}
                          component={Breadcrumbs.ModelFairnessBreadcrumb}
                          allowedRoles={RBAC.PAGES.MODELS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.MODEL_ROI_CALCULATOR_CMP}
                          component={Breadcrumbs.ModelComparisonBreadcrumb}
                          allowedRoles={RBAC.PAGES.MODELS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.REAL_TIME_API}
                          component={Breadcrumbs.RtApiBreadcrumb}
                          allowedRoles={RBAC.PAGES.REAL_TIME_API}
                        />
                        <BreadcrumbRoute
                          exact
                          path={[ROUTES.REAL_TIME_API_DOCUMENTATION, ROUTES.REAL_TIME_API_DOCUMENTATION_CONFIG]}
                          component={Breadcrumbs.RtApiDocumentationBreadcrumb}
                          allowedRoles={RBAC.PAGES.REAL_TIME_API}
                        />
                        <BreadcrumbRoute
                          exact
                          path={[ROUTES.REAL_TIME_API_USAGE, ROUTES.REAL_TIME_API_USAGE_CONFIG]}
                          component={Breadcrumbs.RtApiUsageBreadcrumb}
                          allowedRoles={RBAC.PAGES.REAL_TIME_API}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.REAL_TIME_API_DEMO}
                          component={Breadcrumbs.RtApiDemoBreadcrumb}
                          allowedRoles={RBAC.PAGES.REAL_TIME_API}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.REAL_TIME_API}
                          component={Breadcrumbs.RtApiNewBreadcrumb}
                          allowedRoles={RBAC.PAGES.REAL_TIME_API}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.COPY_PX}
                          component={Breadcrumbs.CopyPersonalizationBreadcrumb}
                          allowedRoles={RBAC.PAGES.COPY_PX}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.VIEW_SEGMENTS}
                          component={Breadcrumbs.SegmentsViewBreadcrumb}
                          allowedRoles={RBAC.PAGES.DATASETS}
                        />
                        <BreadcrumbRoute
                          exact
                          path={ROUTES.REGULATORY}
                          component={Breadcrumbs.RegulatoryBreadcrumb}
                          allowedRoles={RBAC.PAGES.REGULATORY}
                        />
                      </BreadcrumbSwitch>,
                    ],
                    rightChildren: [
                      <Icon
                        key="explain-icon"
                        name="InfoSerif"
                        viewBox="0 0 24 24"
                        data-debug="explain-this-page-icon"
                        m={{ x: '1rem' }}
                        size="1.5rem"
                        data-tip="Explain this page"
                        cursor="pointer"
                      />,
                      <Feedback key="feeedback-component" />,
                      <Div
                        minH="1.5rem"
                        minW="1px"
                        border={{ r: '1px solid' }}
                        borderColor="shade66"
                        key="header-spacer"
                      />,
                      clients?.length > 1 ? (
                        <Div d="inline-flex" align="center" key="list-label">
                          <Text m={{ x: '1.5rem' }} textWeight="500" textColor="shade66">
                            Client
                          </Text>
                        </Div>
                      ) : null,
                      <ClientsSelection
                        key="client-selection"
                        loading={fetchClientStatus === CONSTANTS.API.STARTED}
                        clients={clients}
                        tenant={tenant || profile?.tenant}
                        onSelectClient={(c) => selectClient(c)}
                      />,
                      <Icon
                        key="logout-btn"
                        name="Logout"
                        data-tip="Log out"
                        m={{ l: '1.875rem', r: '1.625rem' }}
                        size="1.25rem"
                        hoverColor="brand400"
                        color="shade66"
                        cursor="pointer"
                        onClick={async () => {
                          await inlineLogout();
                          localStorage.removeItem('accessToken');
                          localStorage.removeItem('refreshToken');
                          window.location.href = `${window.location.origin}/login/${
                            localStorage.getItem('login_client') || ''
                          }`;
                        }}
                      />,
                    ],
                  }}
                  menu={{
                    mini,
                    menuStyle: {
                      zIndex: '10',
                      pos: 'absolute',
                      bg: 'shade33',
                    },
                    options: menu.filter((item) => {
                      if (!client || !profile) return false;
                      if (item.requiresSftp) return sftpEnabled && item.isVisible(client, profile);
                      return item.isVisible(client, profile);
                    }),
                    logo: {
                      largeLogo: <Image src="/static/on-point.svg" />,
                      smallLogo: <Image src="/static/on-point-icon.svg" />,
                    },
                    footerLogo: mini ? null : <Image src="/static/pinpoint.svg" />,
                    footerStyle: {
                      p: { x: mini ? '0rem' : '2rem', y: '2rem' },
                    },
                    isHighlighted: (pathname, link) => pathname.startsWith(link),
                  }}
                  content={{
                    mini,
                    contentStyle: {
                      bg: 'secondary',
                      h: 'calc(100vh - 4.6875rem)',
                    },
                    children: [
                      <Switch key="content-switch">
                        <ProtectedRoute exact path={ROUTES.HOME} component={Pages.Home} />
                        <ProtectedRoute
                          exact
                          path={ROUTES.ONBOARDING_LANDING}
                          component={Pages.OnboardingLanding}
                          allowedRoles={RBAC.PAGES.ONBOARDING}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.ONBOARDING_VIEW}
                          component={Pages.OnboardingView}
                          allowedRoles={RBAC.PAGES.ONBOARDING}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.ONBOARDING}
                          component={Pages.OnboardingList}
                          allowedRoles={RBAC.PAGES.ONBOARDING}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.NEW_ONBOARDING}
                          component={Pages.Onboarding}
                          allowedRoles={RBAC.PAGES.ONBOARDING}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.RE_ONBOARDING}
                          component={Pages.ReOnboarding}
                          allowedRoles={RBAC.PAGES.ONBOARDING}
                        />
                        <Route
                          exact
                          path={ROUTES.DATA_ENRICHMENT_DOWNLOAD}
                          component={Pages.DataEnrichmentDownload}
                          allowedRoles={RBAC.PAGES.ONBOARDING}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.GENERATE_MODELS}
                          component={Pages.GenerateModels}
                          allowedRoles={RBAC.PAGES.ONBOARDING}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.USERS}
                          component={Pages.Users}
                          allowedRoles={RBAC.PAGES.USERS}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.USERS_ADD}
                          component={Pages.AddUser}
                          allowedRoles={RBAC.PAGES.USERS}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.USERS_VIEW}
                          component={Pages.EditUser}
                          allowedRoles={RBAC.PAGES.USERS}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.SEGMENTS}
                          component={Pages.Segments}
                          allowedRoles={RBAC.PAGES.DATASETS}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.COMPARE_SEGMENTS}
                          component={Pages.CompareSegments}
                          allowedRoles={RBAC.PAGES.DATASETS}
                        />
                        <ProtectedRoute
                          exact
                          path="/segments/:segmentId/pred-model"
                          component={Pages.SegmentsPredModelView}
                          allowedRoles={RBAC.PAGES.DATASETS}
                        />
                        <ProtectedRoute
                          exact
                          path="/segments/:segmentId/facets-view"
                          component={Pages.SegmentsFacetsView}
                          allowedRoles={RBAC.PAGES.DATASETS}
                        />
                        <ProtectedRoute
                          exact
                          path="/segments/:segmentId/personality-view"
                          component={Pages.SegmentsOceanView}
                          allowedRoles={RBAC.PAGES.DATASETS}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.CLUSTERS}
                          component={Pages.Clusters}
                          allowedRoles={RBAC.PAGES.DATASETS}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.MODELS}
                          component={Pages.ListModelsPage}
                          allowedRoles={RBAC.PAGES.MODELS}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.MODELS_COMPARE}
                          component={Pages.CompareModelsPage}
                          allowedRoles={RBAC.PAGES.MODELS}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.MODEL_INPUT_DISTRIBUTIONS}
                          component={Pages.InputDistributionsModelsPage}
                          allowedRoles={RBAC.PAGES.MODELS}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.MODEL_VALIDATION_ANALYTICS}
                          component={Pages.ValidationAnalyticsModelsPage}
                          allowedRoles={RBAC.PAGES.MODELS}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.MODEL_ROI_CALCULATOR_CMP}
                          component={Pages.CompareRoiCalculatorPage}
                          allowedRoles={RBAC.PAGES.MODELS}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.MODEL_ROI_CALCULATOR}
                          component={Pages.RoiCalculatorModelsPage}
                          allowedRoles={RBAC.PAGES.MODELS}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.MODEL_FAIRNESS}
                          component={Pages.FairnessModelsPage}
                          allowedRoles={RBAC.PAGES.MODELS}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.MODEL_LIFT_CHARTS}
                          component={Pages.LiftChartsModelsPage}
                          allowedRoles={RBAC.PAGES.MODELS}
                        />
                        <ProtectedRoute
                          exact
                          path={[ROUTES.REAL_TIME_API_USAGE, ROUTES.REAL_TIME_API_USAGE_CONFIG]}
                          component={Pages.RtApiUsage}
                          allowedRoles={RBAC.PAGES.REAL_TIME_API}
                        />
                        <ProtectedRoute
                          exact
                          path={[ROUTES.REAL_TIME_API_DOCUMENTATION, ROUTES.REAL_TIME_API_DOCUMENTATION_CONFIG]}
                          component={Pages.RtApiDocumentation}
                          allowedRoles={RBAC.PAGES.REAL_TIME_API}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.REAL_TIME_API}
                          component={Pages.RtApiList}
                          allowedRoles={RBAC.PAGES.REAL_TIME_API}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.REAL_TIME_API_DEMO}
                          component={Pages.RtApiDemo}
                          allowedRoles={RBAC.PAGES.REAL_TIME_API}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.COPY_PX}
                          component={Pages.CopyPx}
                          allowedRoles={RBAC.PAGES.COPY_PX}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.SFTP}
                          component={Pages.ClientSftpList}
                          allowedRoles={RBAC.PAGES.SFTP}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.CLIENTS}
                          component={Pages.Clients}
                          allowedRoles={RBAC.PAGES.CLIENTS}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.CLIENTS_ADD}
                          component={Pages.AddClient}
                          allowedRoles={RBAC.PAGES.CLIENTS}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.CLIENTS_EDIT}
                          component={Pages.EditClient}
                          allowedRoles={RBAC.PAGES.CLIENTS}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.CLIENTS_EDIT_SUBS}
                          component={Pages.EditClientSubs}
                          allowedRoles={RBAC.PAGES.CLIENTS}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.VIEW_SEGMENTS}
                          component={Pages.SegmentsView}
                          allowedRoles={RBAC.PAGES.DATASETS}
                        />
                        <ProtectedRoute
                          exact
                          path={ROUTES.REGULATORY}
                          component={Pages.Regulatory}
                          allowedRoles={RBAC.PAGES.REGULATORY}
                        />
                        <Route exact path="/token" component={TokenExchange} />
                        <Route path="/" render={() => <Redirect to="/onboarding_landing" />} />
                      </Switch>,
                    ],
                  }}
                />
              )}
            </DndProvider>
          </ErrorBoundary>
        </ThemeProvider>
      </SplitFactory>
    </BrowserRouter>
  );
}

App.propTypes = {
  app: PropTypes.shape({
    mini: PropTypes.bool.isRequired,
    fetchClientStatus: PropTypes.bool.isRequired,
    clients: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
      })
    ).isRequired,
    profile: PropTypes.shape({
      email: PropTypes.string,
      username: PropTypes.string,
      tenant: PropTypes.string,
      originalTenant: PropTypes.string,
      role: PropTypes.string,
      siteRole: PropTypes.string,
      givenName: PropTypes.string,
      familyName: PropTypes.string,
    }).isRequired,
    client: PropTypes.shape({}),
    tenant: PropTypes.string,
  }).isRequired,
  auth: PropTypes.shape({
    challengeName: PropTypes.string,
  }).isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  selectClient: PropTypes.func.isRequired,
  toggleMenu: PropTypes.func.isRequired,
};

const mapStateToProps = ({ auth, app }) => ({
  app,
  auth,
  isLoggedIn: isLoggedInSelector(auth),
});

const mapDispatchToProps = (dispatch) => ({
  toggleMenu: () => dispatch(appActions.toggleMenu()),
  selectClient: (client) => dispatch(appActions.selectClient(client)),
});

export default withSplitFactory(SPLITIO_CONFIG)(connect(mapStateToProps, mapDispatchToProps)(App));
